import {
  TAnyStore,
  TBrand,
  TBuyflowPaths,
  TCurrency,
  TLocale,
  TMarket,
  TOverchatProps
} from 'types/common/common.types'

import {
  BUYFLOW_PATHS,
  SESSION_STORAGE_KEYS,
  localeToIntlLocaleMap,
  localeToStoreMap
} from 'utils/constants/common'

const VALID_LOCALES: TLocale[] = [
  'br',
  'ar',
  'uy',
  'co',
  'cl',
  'ec',
  'pe',
  'mx'
]

/**
 * Transforms an ISO/Intl locale into the app's short locale code.
 *
 * @example
 * intlLocaleToLocale('es-AR') // 'ar'
 * intlLocaleToLocale('pt-BR') // 'br'
 *
 * Falls back to the i18n default locale ('ar') when the input is missing
 * or does not map to a known {@link TLocale}.
 */
export const intlLocaleToLocale = (intlLocale?: string | null): TLocale => {
  const region = intlLocale?.split('-')[1]?.toLowerCase() as TLocale | undefined

  return region && VALID_LOCALES.includes(region) ? region : 'ar'
}

export const getStoreBasedOnLocale = (
  locale: TLocale,
  partner?: string | null
): TAnyStore => {
  const store = localeToStoreMap[locale]

  if (!partner) return store

  /**
   * Save value of store in session for vanities.
   * This is because the only entry point for a vanity is the registration page,
   * in each subsequent step then the user can loose the param, but still needs the store.
   */
  if (typeof window !== 'undefined') {
    sessionStorage.setItem(
      SESSION_STORAGE_KEYS.STORE,
      `${store}_${partner.toUpperCase()}`
    )
  }

  return `${store}_${partner.toUpperCase()}`
}

export interface IDisplayPriceByLocaleOptions {
  price: string
  locale: TLocale
  currency: TCurrency
  showSymbol?: boolean
  hideZeroDecimals?: boolean
}

export const displayPriceByLocale = (
  options: IDisplayPriceByLocaleOptions
): string => {
  const {
    price,
    locale,
    currency,
    showSymbol = true,
    hideZeroDecimals = true
  } = options

  // Remove thousand separators (commas)
  // Values from Magento are coming in the format '29,000.00'
  const normalized = price.includes(',') ? price.replace(/,/g, '') : price
  const value = Number.parseFloat(normalized)
  const intLocale = localeToIntlLocaleMap[locale]

  // Check if value has .00 decimals and hideZeroDecimals is enabled
  const hasNoDecimals = hideZeroDecimals && value % 1 === 0

  return new Intl.NumberFormat(intLocale, {
    style: showSymbol ? 'currency' : undefined,
    currency,
    minimumFractionDigits: hasNoDecimals ? 0 : 2,
    maximumFractionDigits: 2
  }).format(value)
}

interface IOptionsHardNavigationToBuyflowPath {
  market: TMarket
  locale: TLocale
  pathKey: keyof TBuyflowPaths
}

export const hardNavigationToBuyflowPath = (
  options: IOptionsHardNavigationToBuyflowPath
): string => {
  const { market, locale, pathKey } = options
  const PATHS = BUYFLOW_PATHS[market]

  return (window.location.href = `/${locale}/${PATHS[pathKey]}`)
}

type TOverchatBaseProps = Omit<
  TOverchatProps,
  | 'countrySelect'
  | 'onOpenChange'
  | 'onStart'
  | 'onError'
  | 'apiKey'
  | 'externalId'
>

const DGOOverchatBaseProps: TOverchatBaseProps = {
  chatTitle: 'DGO',
  consumerSystem: 'WidgetChatBotDgo',
  elementId: 'overchat-root',
  fileAttachment: true,
  language: 'es',
  position: 'bottom-right',
  primaryColor: '#077cc0',
  theme: 'dark',
  env: 'prd'
}

const SKYMAISOverchatBaseProps: TOverchatBaseProps = {
  chatTitle: 'SKY+',
  consumerSystem: 'WidgetChatBotSkyMais',
  elementId: 'overchat-root',
  fileAttachment: true,
  language: 'pt',
  position: 'bottom-right',
  primaryColor: '#e81c24',
  theme: 'dark',
  env: 'prd'
}

export const loadOverchatBasePropsPerBrand = (
  brand: TBrand
): TOverchatBaseProps | null => {
  if (!brand) return null

  if (brand === 'dgo') {
    return DGOOverchatBaseProps
  }

  return SKYMAISOverchatBaseProps
}
export const formatDate = (date: string | Date) => {
  const parsedDate = typeof date === 'string' ? new Date(date) : date

  return parsedDate.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  })
}
